import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { 
  Search, Brain, Download, Calendar, User, CheckCircle, 
  XCircle, Clock, TrendingUp, BarChart3, ChevronDown, ChevronUp, ClipboardList
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface PsychometricRecord {
  id: string;
  display_name: string;
  full_name: string;
  team: string;
  position: string;
  site_id: string | null;
  site_name?: string;
  psychometric_profile: Record<string, any> | null;
  baseline_risk_score: number | null;
  created_at: string;
  updated_at: string;
}

interface PsychometricStats {
  totalWorkers: number;
  assessedCount: number;
  pendingCount: number;
  avgBaselineRisk: number;
  recentAssessments: number;
}

interface AdminPsychometricsProps {
  siteId?: string | null;
}

const AdminPsychometrics = ({ siteId: propSiteId }: AdminPsychometricsProps = {}) => {
  const { toast } = useToast();
  const [records, setRecords] = useState<PsychometricRecord[]>([]);
  const [stats, setStats] = useState<PsychometricStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState(propSiteId || 'all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'assessed' | 'pending'>('all');
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(null);

  useEffect(() => {
    fetchPsychometricData();
  }, [selectedSite]);

  const fetchPsychometricData = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('worker_profiles')
        .select(`
          id, display_name, full_name, team, position, site_id,
          psychometric_profile, baseline_risk_score,
          created_at, updated_at,
          site:company_locations!site_id(location_name)
        `)
        .order('updated_at', { ascending: false });

      if (selectedSite !== 'all') {
        query = query.eq('site_id', selectedSite);
      }

      const { data, error } = await query;
      if (error) throw error;

      const transformedRecords: PsychometricRecord[] = (data || []).map((r: any) => ({
        id: r.id,
        display_name: r.display_name,
        full_name: r.full_name || '',
        team: r.team,
        position: r.position || '',
        site_id: r.site_id,
        site_name: r.site?.location_name,
        psychometric_profile: r.psychometric_profile,
        baseline_risk_score: r.baseline_risk_score,
        baseline_calculated_at: r.baseline_calculated_at,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));

      setRecords(transformedRecords);

      const total = transformedRecords.length;
      const assessed = transformedRecords.filter(r => 
        r.psychometric_profile && Object.keys(r.psychometric_profile).length > 0
      ).length;
      const pending = total - assessed;
      
      const withBaseline = transformedRecords.filter(r => r.baseline_risk_score !== null);
      const avgBaseline = withBaseline.length > 0 
        ? withBaseline.reduce((sum, r) => sum + (r.baseline_risk_score || 0), 0) / withBaseline.length 
        : 0;

      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const recent = transformedRecords.filter(r => 
        r.psychometric_profile && r.updated_at && new Date(r.updated_at) > weekAgo
      ).length;

      setStats({
        totalWorkers: total,
        assessedCount: assessed,
        pendingCount: pending,
        avgBaselineRisk: Math.round(avgBaseline),
        recentAssessments: recent,
      });

    } catch (error) {
      console.error('Error fetching psychometric data:', error);
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Name', 'Team', 'Position', 'Site', 
      'Negative Urgency', 'Lack of Premeditation', 'Lack of Perseverance', 
      'Sensation Seeking', 'Positive Urgency', 'DOSPERT H/S', 'Delay Discounting',
      'Baseline Risk Score', 'Assessment Date'
    ];

    const rows = records
      .filter(r => r.psychometric_profile && Object.keys(r.psychometric_profile).length > 0)
      .map(r => {
        const upps = r.psychometric_profile?.upps_p || {};
        return [
          r.display_name,
          r.team,
          r.position,
          r.site_name || '',
          upps.negative_urgency || '',
          upps.lack_of_premeditation || '',
          upps.lack_of_perseverance || '',
          upps.sensation_seeking || '',
          upps.positive_urgency || '',
          r.psychometric_profile?.dospert_health_safety || '',
          r.psychometric_profile?.delay_discounting_k || '',
          r.baseline_risk_score || '',
          r.updated_at ? format(new Date(r.updated_at), 'yyyy-MM-dd') : ''
        ].join(',');
      });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `psychometric-data-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Export Complete', description: `Exported ${rows.length} records` });
  };

  const hasProfile = (r: PsychometricRecord) => 
    r.psychometric_profile && Object.keys(r.psychometric_profile).length > 0;

  const filteredRecords = records.filter(r => {
    const matchesSearch = 
      r.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.team.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'assessed' && hasProfile(r)) ||
      (filterStatus === 'pending' && !hasProfile(r));

    return matchesSearch && matchesFilter;
  });

  const formatScore = (score: number | undefined) => {
    if (score === undefined || score === null) return null;
    return Math.round(score * 100);
  };

  const calculateDisplayBaseline = (record: PsychometricRecord): number | null => {
    if (record.baseline_risk_score && record.baseline_risk_score > 0) {
      return record.baseline_risk_score;
    }
    
    const profile = record.psychometric_profile;
    if (!profile || !profile.upps_p) return null;
    
    const upps = profile.upps_p;
    const lackOfPerseverance = upps.lack_of_perseverance || 0;
    const negativeUrgency = upps.negative_urgency || 0;
    const positiveUrgency = upps.positive_urgency || 0;
    const lackOfPremeditation = upps.lack_of_premeditation || 0;
    const sensationSeeking = upps.sensation_seeking || 0;
    const dospertHealthSafety = profile.dospert_health_safety || 0;
    const delayDiscounting = profile.delay_discounting_k || 0;
    
    const otherUpps = (positiveUrgency + lackOfPremeditation + sensationSeeking) / 3;
    const baseline = Math.round(
      (lackOfPerseverance * 0.40 +
       negativeUrgency * 0.25 +
       otherUpps * 0.10 +
       delayDiscounting * 0.10 +
       dospertHealthSafety * 0.15) * 100
    );
    
    return baseline;
  };

  const getCellColor = (score: number | undefined) => {
    if (score === undefined || score === null) return 'text-muted-foreground';
    const pct = score * 100;
    if (pct >= 70) return 'text-destructive font-bold';
    if (pct >= 50) return 'text-warning font-bold';
    if (pct >= 30) return 'text-yellow-600 font-semibold';
    return 'text-success';
  };

  const getBaselineLabel = (baseline: number) => {
    if (baseline >= 60) return { label: 'Risiko Tinggi', className: 'bg-destructive/20 text-destructive border-destructive/30' };
    if (baseline >= 35) return { label: 'Risiko Sedang', className: 'bg-warning/20 text-warning border-warning/30' };
    return { label: 'Risiko Rendah', className: 'bg-success/20 text-success border-success/30' };
  };

  // Generate psychologist-style narrative notes
  const generatePsychologistNotes = (record: PsychometricRecord): { summaryParts: string[]; scenario: string; recommendation: string; riskLevel: string } => {
    const profile = record.psychometric_profile;
    if (!profile || !profile.upps_p) {
      return { summaryParts: [], scenario: '', recommendation: '', riskLevel: 'unknown' };
    }

    const upps = profile.upps_p;
    const nu = Math.round((upps.negative_urgency || 0) * 100);
    const lp = Math.round((upps.lack_of_perseverance || 0) * 100);
    const lprem = Math.round((upps.lack_of_premeditation || 0) * 100);
    const ss = Math.round((upps.sensation_seeking || 0) * 100);
    const pu = Math.round((upps.positive_urgency || 0) * 100);
    const dospert = Math.round((profile.dospert_health_safety || 0) * 100);
    const dd = Math.round((profile.delay_discounting_k || 0) * 100);
    const baseline = calculateDisplayBaseline(record) || 0;
    const interventionStyle = profile.intervention_style || null;

    const riskLevel = baseline >= 60 ? 'high' : baseline >= 35 ? 'moderate' : 'low';
    const summaryParts: string[] = [];
    const highTraits: string[] = [];

    // Opening characterization
    if (riskLevel === 'high') {
      summaryParts.push(`${record.display_name} menunjukkan profil risiko tinggi (skor dasar ${baseline}%) yang memerlukan perhatian khusus dari supervisor.`);
    } else if (riskLevel === 'moderate') {
      summaryParts.push(`${record.display_name} memiliki profil risiko sedang (skor dasar ${baseline}%) dengan beberapa kecenderungan perilaku yang perlu diperhatikan.`);
    } else {
      summaryParts.push(`${record.display_name} menunjukkan profil risiko rendah (skor dasar ${baseline}%). Secara umum, individu ini memiliki kontrol diri yang baik dan cenderung mengikuti prosedur keselamatan.`);
    }

    // Weave traits into narrative
    if (nu >= 30 && lp >= 30) {
      summaryParts.push(`Ketika berada di bawah tekanan atau sedang emosi, individu ini cenderung bertindak gegabah tanpa berpikir panjang (Negative Urgency: ${nu}%). Ditambah dengan kecenderungan cepat menyerah menghadapi tugas yang membutuhkan ketelitian (Lack of Perseverance: ${lp}%), ada risiko tinggi bahwa langkah-langkah keselamatan yang dianggap "ribet" akan dilewatkan, terutama saat shift sudah melelahkan.`);
      highTraits.push('nu', 'lp');
    } else {
      if (nu >= 30) {
        summaryParts.push(`Individu ini mudah terbawa emosi dan cenderung bertindak impulsif saat marah, stres, atau frustrasi (Negative Urgency: ${nu}%). Dalam situasi tegang — misalnya setelah konflik dengan rekan atau tekanan deadline — kemampuan mengendalikan diri menurun drastis.`);
        highTraits.push('nu');
      }
      if (lp >= 30) {
        summaryParts.push(`Ada kecenderungan untuk cepat bosan dan menyerah saat menghadapi tugas rutin yang membutuhkan fokus berkelanjutan (Lack of Perseverance: ${lp}%). Prosedur keselamatan yang panjang atau berulang, seperti checklist inspeksi harian, berpotensi tidak diselesaikan secara menyeluruh.`);
        highTraits.push('lp');
      }
    }

    if (lprem >= 30) {
      summaryParts.push(`Individu ini sering bertindak tanpa memikirkan konsekuensi terlebih dahulu (Lack of Premeditation: ${lprem}%). Keputusan diambil secara spontan — termasuk keputusan yang menyangkut keselamatan — tanpa menimbang dampak jangka panjang.`);
      highTraits.push('lprem');
    }
    if (ss >= 30) {
      summaryParts.push(`Terdapat dorongan kuat untuk mencari sensasi dan pengalaman baru (Sensation Seeking: ${ss}%). Di lingkungan kerja, ini bisa terwujud dalam kecenderungan mengambil "jalan pintas" yang lebih berisiko demi menghindari kebosanan.`);
      highTraits.push('ss');
    }
    if (pu >= 30) {
      summaryParts.push(`Menariknya, individu ini juga menunjukkan impulsivitas saat suasana hati sedang baik (Positive Urgency: ${pu}%). Saat merasa percaya diri — misalnya setelah menyelesaikan pekerjaan lebih cepat — ada kecenderungan menjadi terlalu optimis dan meremehkan risiko.`);
      highTraits.push('pu');
    }
    if (dospert >= 30) {
      summaryParts.push(`Dari segi persepsi risiko, individu ini secara sadar menerima tingkat risiko kesehatan dan keselamatan yang lebih tinggi dibanding rata-rata (DOSPERT: ${dospert}%). Bukan berarti tidak tahu bahayanya — tapi lebih memilih untuk "menerima" risiko tersebut sebagai hal yang wajar.`);
      highTraits.push('dospert');
    }
    if (dd >= 30) {
      summaryParts.push(`Ada preferensi kuat untuk memilih keuntungan jangka pendek dibanding manfaat jangka panjang (Delay Discounting: ${dd}%). Lebih memilih menyelesaikan pekerjaan lebih cepat sekarang meskipun harus melewatkan langkah keselamatan.`);
      highTraits.push('dd');
    }

    // Protective factors
    const protectiveFactors: string[] = [];
    if (nu < 30) protectiveFactors.push('kontrol emosi yang baik');
    if (lp < 30) protectiveFactors.push('ketekunan menyelesaikan tugas');
    if (lprem < 30) protectiveFactors.push('kemampuan berpikir sebelum bertindak');
    if (ss < 30) protectiveFactors.push('tidak terdorong mencari sensasi berlebihan');
    if (dospert < 30) protectiveFactors.push('kesadaran risiko yang sehat');
    if (dd < 30) protectiveFactors.push('kemampuan melihat dampak jangka panjang');
    if (protectiveFactors.length > 0 && highTraits.length > 0) {
      summaryParts.push(`Sebagai faktor pelindung, individu ini memiliki ${protectiveFactors.join(', ')}.`);
    }

    // Scenario
    let scenario = '';
    if (riskLevel === 'high') {
      if (highTraits.includes('nu') && highTraits.includes('lp')) {
        scenario = `Bayangkan situasi: shift sudah berjalan 10 jam, ada tekanan untuk mengejar target produksi. ${record.display_name} mendapat teguran dari supervisor karena pekerjaan lambat. Dalam kondisi emosi seperti ini, sangat mungkin individu ini akan memotong prosedur lockout/tagout, berpikir "sudah pernah aman sebelumnya," dan langsung melanjutkan pekerjaan tanpa langkah pengamanan.`;
      } else if (highTraits.includes('ss') && highTraits.includes('dospert')) {
        scenario = `Bayangkan situasi: ada mesin yang butuh perbaikan minor tapi dalam kondisi masih beroperasi. ${record.display_name} kemungkinan besar akan memilih untuk "perbaiki sambil jalan" — melepas pelindung mesin untuk akses lebih cepat — karena merasa risiko tersebut masih bisa diterima.`;
      } else {
        scenario = `Bayangkan situasi: akhir shift, semua orang sudah kelelahan dan ingin pulang. Ada satu langkah inspeksi akhir yang harus dilakukan. ${record.display_name} sangat mungkin akan mengisi checklist tanpa benar-benar memeriksa, atau bahkan melewatkannya sama sekali — terutama jika rekan-rekan kerja di sekitarnya juga melakukan hal yang sama.`;
      }
    } else if (riskLevel === 'moderate') {
      scenario = `Dalam kondisi normal, ${record.display_name} kemungkinan besar akan mengikuti prosedur keselamatan dengan baik. Namun perlu diwaspadai saat ada faktor pemicu seperti tekanan waktu, kelelahan, atau konflik di tempat kerja — di saat itulah kecenderungan impulsif bisa muncul.`;
    } else {
      scenario = `${record.display_name} secara umum adalah pekerja yang bisa diandalkan dari segi keselamatan. Kemampuan menahan diri, kesabaran, dan kesadaran risiko yang dimilikinya menjadi "benteng" alami terhadap perilaku tidak aman.`;
    }

    // Recommendation
    let recommendation = '';
    if (interventionStyle === 'social_norm') {
      recommendation = `Pendekatan yang paling efektif adalah melalui norma sosial — tunjukkan bahwa rekan-rekan kerjanya sudah bekerja dengan aman. Peer pressure positif dan pengakuan publik atas perilaku aman akan sangat berpengaruh.`;
    } else if (interventionStyle === 'loss_frame') {
      recommendation = `Individu ini paling responsif terhadap framing kerugian — jelaskan apa yang bisa hilang (gaji, posisi, hubungan dengan keluarga) jika mengabaikan keselamatan. Fokus pada konsekuensi negatif yang konkret dan personal.`;
    } else if (interventionStyle === 'commitment') {
      recommendation = `Pendekatan komitmen personal akan paling efektif — minta individu ini membuat janji spesifik tentang perilaku aman yang akan dilakukan. Mekanisme psikologis "consistency" akan membantu menjaga perilaku aman.`;
    } else if (interventionStyle === 'micro_learning') {
      recommendation = `Pelatihan singkat dan terstruktur (micro-learning) adalah pendekatan terbaik. Berikan materi 2-3 menit yang fokus pada satu topik spesifik dengan contoh nyata.`;
    } else if (highTraits.includes('nu')) {
      recommendation = `Perlu perhatian khusus saat individu ini sedang dalam kondisi emosional. Supervisor sebaiknya melakukan check-in singkat di awal shift untuk mengukur mood. Hindari konfrontasi langsung — gunakan pendekatan empati.`;
    } else if (highTraits.includes('ss')) {
      recommendation = `Libatkan individu ini dalam tugas-tugas yang menantang tapi aman — misalnya menjadi anggota tim tanggap darurat atau safety champion. Salurkan kebutuhan sensasi ke jalur yang produktif.`;
    } else if (highTraits.includes('lp')) {
      recommendation = `Sederhanakan prosedur keselamatan dan buat lebih menarik secara visual. Gunakan checklist pendek dengan langkah-langkah yang jelas. Berikan feedback segera setelah tugas selesai.`;
    } else {
      recommendation = `Lanjutkan monitoring rutin dan berikan apresiasi atas kepatuhan terhadap prosedur keselamatan. Individu ini bisa menjadi contoh positif bagi rekan-rekan kerjanya.`;
    }

    return { summaryParts, scenario, recommendation, riskLevel };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            Psychometric Assessments
          </h2>
          <p className="text-muted-foreground">
            Raw psychometric data from worker onboarding assessments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SiteFilter value={selectedSite} onChange={setSelectedSite} />
          <Button onClick={exportToCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Workers</p>
                <p className="text-3xl font-bold">{stats?.totalWorkers}</p>
              </div>
              <User className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-success/10 to-success/5 border-success/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Assessed</p>
                <p className="text-3xl font-bold text-success">{stats?.assessedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-success/50" />
            </div>
            <Progress 
              value={stats?.totalWorkers ? (stats.assessedCount / stats.totalWorkers) * 100 : 0} 
              className="h-1.5 mt-2" 
            />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-3xl font-bold text-warning">{stats?.pendingCount}</p>
              </div>
              <XCircle className="h-8 w-8 text-warning/50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Avg Baseline Risk</p>
                <p className={cn(
                  "text-3xl font-bold",
                  (stats?.avgBaselineRisk || 0) >= 60 ? "text-destructive" :
                  (stats?.avgBaselineRisk || 0) >= 30 ? "text-warning" : "text-success"
                )}>{stats?.avgBaselineRisk}%</p>
              </div>
              <BarChart3 className="h-8 w-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">This Week</p>
                <p className="text-3xl font-bold">{stats?.recentAssessments}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">new assessments</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(['all', 'assessed', 'pending'] as const).map((filter) => (
                <Button
                  key={filter}
                  variant={filterStatus === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(filter)}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <span className="text-sm font-medium">Risk Level:</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-destructive/20 border border-destructive/30" />
            <span className="text-xs">High (≥70%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-warning/20 border border-warning/30" />
            <span className="text-xs">Medium (50-69%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-yellow-500/20 border border-yellow-500/30" />
            <span className="text-xs">Low-Med (30-49%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-success/20 border border-success/30" />
            <span className="text-xs">Low (&lt;30%)</span>
          </div>
        </div>
      </Card>

      {/* Records Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Psychometric Heatmap</CardTitle>
          <p className="text-xs text-muted-foreground">Klik baris pekerja untuk melihat analisis karakteristik individu</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                   <th className="text-left py-3 px-3 font-medium sticky left-0 bg-muted/30">Worker</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[110px] border-l-2 border-primary bg-primary/5">
                     <span className="text-primary font-bold">Baseline</span>
                   </th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">Neg. Urg.</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">Lack Prem.</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">Lack Pers.</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">Sensation</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">Pos. Urg.</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">DOSPERT</th>
                   <th className="text-center py-3 px-2 font-medium min-w-[70px]">Delay Disc.</th>
                 </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => {
                  const upps = record.psychometric_profile?.upps_p || {};
                  const assessed = hasProfile(record);
                  
                  if (!assessed) return null;
                  
                  const isExpanded = expandedWorkerId === record.id;
                  
                  return (
                    <>
                      <tr 
                        key={record.id} 
                        className={cn(
                          "border-b hover:bg-muted/20 cursor-pointer transition-colors",
                          isExpanded && "bg-muted/10"
                        )}
                        onClick={() => setExpandedWorkerId(isExpanded ? null : record.id)}
                      >
                        <td className="py-2 px-3 sticky left-0 bg-card">
                           <div className="flex items-center gap-2">
                             <div className="flex-1">
                               <p className="font-semibold">{record.display_name}</p>
                               <p className="text-xs text-muted-foreground">
                                 {record.team} • {record.site_name || 'No site'}
                               </p>
                             </div>
                             {isExpanded ? (
                               <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                             ) : (
                               <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                             )}
                           </div>
                         </td>
                         <td className="py-2 px-1 text-center border-l-2 border-primary bg-primary/5">
                           {(() => {
                             const baseline = calculateDisplayBaseline(record);
                             if (baseline !== null) {
                               const { label, className: badgeClass } = getBaselineLabel(baseline);
                               return (
                                 <span className={cn(
                                   "inline-block rounded-full px-3 py-1 text-xs font-bold border",
                                   badgeClass
                                 )}>
                                   {label}
                                 </span>
                               );
                             }
                             return <span className="text-muted-foreground">-</span>;
                           })()}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(upps.negative_urgency))}>
                           {formatScore(upps.negative_urgency) !== null ? `${formatScore(upps.negative_urgency)}%` : '-'}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(upps.lack_of_premeditation))}>
                           {formatScore(upps.lack_of_premeditation) !== null ? `${formatScore(upps.lack_of_premeditation)}%` : '-'}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(upps.lack_of_perseverance))}>
                           {formatScore(upps.lack_of_perseverance) !== null ? `${formatScore(upps.lack_of_perseverance)}%` : '-'}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(upps.sensation_seeking))}>
                           {formatScore(upps.sensation_seeking) !== null ? `${formatScore(upps.sensation_seeking)}%` : '-'}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(upps.positive_urgency))}>
                           {formatScore(upps.positive_urgency) !== null ? `${formatScore(upps.positive_urgency)}%` : '-'}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(record.psychometric_profile?.dospert_health_safety))}>
                           {formatScore(record.psychometric_profile?.dospert_health_safety) !== null 
                             ? `${formatScore(record.psychometric_profile?.dospert_health_safety)}%` 
                             : '-'}
                         </td>
                         <td className={cn("py-2 px-1 text-center font-mono text-sm", getCellColor(record.psychometric_profile?.delay_discounting_k))}>
                           {formatScore(record.psychometric_profile?.delay_discounting_k) !== null 
                             ? `${formatScore(record.psychometric_profile?.delay_discounting_k)}%` 
                             : '-'}
                         </td>
                      </tr>
                      {/* Expanded Psychologist Notes Panel */}
                      {isExpanded && (
                        <tr key={`${record.id}-expanded`}>
                          <td colSpan={9} className="p-0">
                            <div className="mx-4 my-3 rounded-lg border border-primary/20 bg-primary/5 overflow-hidden">
                              {/* Header */}
                              <div className="flex items-center gap-2 px-5 py-3 border-b border-primary/10 bg-primary/10">
                                <ClipboardList className="h-5 w-5 text-primary" />
                                <span className="font-bold text-primary">📋 Karakteristik Individu</span>
                                <Badge variant="outline" className={cn(
                                  "ml-auto text-xs",
                                  generatePsychologistNotes(record).riskLevel === 'high' 
                                    ? "border-destructive text-destructive" 
                                    : generatePsychologistNotes(record).riskLevel === 'moderate'
                                    ? "border-warning text-warning"
                                    : "border-success text-success"
                                )}>
                                  {generatePsychologistNotes(record).riskLevel === 'high' ? 'Risiko Tinggi' 
                                    : generatePsychologistNotes(record).riskLevel === 'moderate' ? 'Risiko Sedang' 
                                    : 'Risiko Rendah'}
                                </Badge>
                              </div>

                              {(() => {
                                const notes = generatePsychologistNotes(record);
                                return (
                                  <div className="px-5 py-4 space-y-4">
                                     {/* Summary narrative */}
                                     <div>
                                       <h4 className="text-xs font-bold uppercase text-muted-foreground tracking-wider mb-2">Profil Perilaku</h4>
                                       <div className="space-y-2">
                                         {notes.summaryParts.map((paragraph, idx) => (
                                           <p key={idx} className="text-sm leading-relaxed text-foreground/90"
                                             dangerouslySetInnerHTML={{
                                               __html: paragraph
                                                 .replace(/\(([^)]+: \d+%)\)/g, '(<strong>$1</strong>)')
                                                 .replace(/(risiko tinggi|risiko sedang|impulsif|gegabah|melewatkan|dilewatkan|memotong prosedur|tanpa langkah pengamanan|cepat menyerah|tanpa berpikir panjang)/gi, '<strong>$1</strong>')
                                             }}
                                           />
                                         ))}
                                       </div>
                                     </div>

                                    {/* Scenario */}
                                    <div className="border-l-2 border-warning/50 pl-4 bg-warning/5 rounded-r-md py-3 pr-3">
                                      <h4 className="text-xs font-bold uppercase text-muted-foreground tracking-wider mb-2">💡 Skenario di Lapangan</h4>
                                      <p className="text-sm leading-relaxed text-foreground/80 italic">
                                        {notes.scenario}
                                      </p>
                                    </div>

                                    {/* Recommendation */}
                                    <div className="border-l-2 border-success/50 pl-4 bg-success/5 rounded-r-md py-3 pr-3">
                                      <h4 className="text-xs font-bold uppercase text-muted-foreground tracking-wider mb-2">✅ Rekomendasi Pendekatan</h4>
                                      <p className="text-sm leading-relaxed text-foreground/80">
                                        {notes.recommendation}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredRecords.filter(r => hasProfile(r)).length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No records match your filters
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPsychometrics;
